<template>
    <div>
        <vue-element-loading :active="isSubmit" />
        <v-card>
            <v-card-title>
                <v-icon color="primary">
                    {{ getIcon(userType) }}
                </v-icon>
                <span class="px-2">{{ $t(userType.toLowerCase().replace(' ', '_')) }}</span>
                <v-spacer></v-spacer>
                <!-- the list on screen, as a spreadsheet: schools were copying
                     rows out of the table by hand to work on them anywhere else -->
                <v-btn
                    class="mx-2"
                    outlined
                    color="secondary"
                    :disabled="isSubmit || currentTabUsers.length === 0"
                    @click="exportCurrentTab"
                >
                    <v-icon class="me-1" small>mdi-download</v-icon>
                    <span>{{ $t('export_csv') }}</span>
                </v-btn>
                <create-button v-if="userType === 'schools'"
                 @create="showCreateSchoolDialog"></create-button>
                 <div v-else-if="userType === 'students'">
                    <!-- one way in for a spreadsheet, and it shows what the
                         file would do before it does any of it -->
                    <v-btn
                        class="mx-2"
                        color="primary"
                        :to="{ name: 'imports', query: { kind: 'students' } }"
                        :disabled="isSubmit"
                    >
                        <v-icon class="mx-2">mdi-file-upload</v-icon>
                        <span>{{ $t('import_from_a_spreadsheet') }}</span>
                    </v-btn>
                    <v-btn
                        class="mx-2"
                        color="success"
                        @click="studentDialog = true"
                        :disabled="isSubmit"
                    >
                        <v-icon class="mx-2">mdi-account-plus</v-icon>
                        <span>{{ $t('add_student') }}</span>
                    </v-btn>
                 </div>
                 <create-button
                    v-else-if="userType === 'guardians'"
                    @create="parentDialog = true"
                 ></create-button>
                 <div v-else-if="userType === 'drivers'">
                    <v-btn
                        class="mx-2"
                        color="primary"
                        :to="{ name: 'imports', query: { kind: 'drivers' } }"
                        :disabled="isSubmit"
                    >
                        <v-icon class="mx-2">mdi-file-upload</v-icon>
                        <span>{{ $t('import_from_a_spreadsheet') }}</span>
                    </v-btn>
                    <create-button @create="driverDialog = true"></create-button>
                 </div>
            </v-card-title>
            <v-tabs v-model="active_tab" show-arrows class="my-2">
                <v-tab v-for="tab in tabs" :key="tab.idx">
                    <v-icon size="20" class="me-3">
                        {{ tab.icon }}
                    </v-icon>
                    <span>{{ tab.title }}</span>
                    <!-- the counts come back with the page, so a school can see
                         it has 12 waiting for review without opening the tab -->
                    <v-chip x-small class="ms-2">{{ tabCount(tab.idx) }}</v-chip>
                </v-tab>
            </v-tabs>
            <!-- One table, not one per tab. The four were identical apart
                 from which list they were handed and which handlers someone
                 remembered to wire up, which is why the review tab could not
                 delete and the out-of-coins tab could not do anything at all. -->
            <users-table
                :users="rows"
                :total="total"
                :serverMode="true"
                :isLoading="isLoading"
                :userType="userType"
                :tab="active_tab"
                :mode="mode"
                @fetch="fetchPage"
                @view-user="viewUser"
                @edit-user="editUser"
                @suspend-user="suspendActivateUser"
                @set-password="showSetPassword"
                @bulk-finished="reload"
                @map-students="showMapStudents"
                @unmap-students="unmapStudents"
                @manage-students="manageGuardianStudents"
                @add-coins="showAddCoins"
                @unassign-bus="unAssignBus"
                @assign-bus="assignBus"
                @set-student-bus="setStudentBus"
                @login-as-school="loginAsSchool"
                @delete-school="deleteSchool"
                @delete-user="deleteUser"
                @approve-user="approveUser"
                @reject-user="rejectUser"
                @show-student-location="showStudentLocation"
            ></users-table>
        </v-card>
        <v-dialog v-if="selectedDriver" v-model="busesDialog" max-width="390">
            <v-card>
                <v-card-title class="text-h5">
                    {{ $t('select_bus_for_driver', { name: selectedDriver.name }) }}
                </v-card-title>

                <v-card-text>
                    <v-list dense>
                        <v-subheader>{{ $t('buses') }}</v-subheader>
                        <v-list-item-group>
                            <v-list-item
                                v-for="(bus, i) in availableBuses"
                                :key="i"
                            >
                                <v-list-item-content
                                    @click="assignBusToDriver(bus)"
                                >
                                    <v-list-item-title>
                                        {{ $t('license') }}: {{ bus.license }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ $t('capacity') }}: {{ bus.capacity }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-card-text>
                <v-container style="height: 400px">
                    <v-row
                        v-show="loadingBuses"
                        class="fill-height"
                        align-content="center"
                        justify="center"
                    >
                        <v-col class="text-subtitle-1 text-center" cols="12">
                            {{ $t('please_wait') }}
                        </v-col>
                        <v-col cols="6">
                            <v-progress-linear
                                :active="loadingBuses"
                                color="primary"
                                indeterminate
                                rounded
                                height="6"
                            ></v-progress-linear>
                        </v-col>
                    </v-row>
                </v-container>
                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="green darken-1" text @click="closeBusDialog">
                        {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-if="selectedStudent" v-model="busesForStudentDialog" max-width="490">
            <v-card>
                <v-card-title class="text-h5">
                    {{ $t('select_bus_for_student', {
                        time: selectedStudentMorning == 1 ? $t('morning') : $t('afternoon'),
                        name: selectedStudent.name
                    }) }}
                </v-card-title>

                <v-card-text>
                    <!-- changing a child's bus is the change families most need
                         to hear about, and it went out silently -->
                    <v-switch
                        v-model="notifyOnBusChange"
                        :label="$t('notify_the_family')"
                        :hint="$t('notify_the_family_hint')"
                        persistent-hint
                        dense
                        inset
                    ></v-switch>
                    <v-list dense>
                        <v-subheader>{{ $t('buses') }}</v-subheader>
                        <v-list-item-group>
                            <v-list-item
                                v-for="(bus, i) in availableBuses"
                                :key="i"
                            >
                                <v-list-item-content
                                    @click="assignBusToStudent(bus)"
                                >
                                    <v-list-item-title>
                                        {{ $t('license') }}: {{ bus.license }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ $t('capacity') }}: {{ bus.capacity }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle>
                                        {{ $t('available_seats') }}: {{ bus.available_seats }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list-item-group>
                    </v-list>
                </v-card-text>
                <v-container style="height: 400px">
                    <v-row
                        v-show="loadingBuses"
                        class="fill-height"
                        align-content="center"
                        justify="center"
                    >
                        <v-col class="text-subtitle-1 text-center" cols="12">
                            {{ $t('please_wait') }}
                        </v-col>
                        <v-col cols="6">
                            <v-progress-linear
                                :active="loadingBuses"
                                color="primary"
                                indeterminate
                                rounded
                                height="6"
                            ></v-progress-linear>
                        </v-col>
                    </v-row>
                </v-container>
                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="green darken-1" text @click="closeStudentBusDialog">
                        {{ $t('close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-row justify="center">
            <v-dialog
                v-model="schoolDialog"
                persistent
                no-click-animation
                max-width="700px"
                @keydown.esc="schoolDialog = false"
            >
                <v-form ref="form" v-model="valid" lazy-validation>
                    <v-card>
                        <v-card-title>
                            <span class="text-h5">{{ $t('school_account') }}</span>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <v-col cols="12" sm="6" md="6">
                                        <v-text-field
                                            v-model="name"
                                            :rules="nameRules"
                                            :label="$t('Name') + '*'"
                                            :hint="$t('name_of_school')"
                                            required
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="6" md="6">
                                        <v-text-field
                                            v-model="email"
                                            :rules="emailRules"
                                            :label="$t('Email') + '*'"
                                            :hint="$t('email_of_school_account')"
                                            required
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" sm="6" md="6">
                                        <v-text-field
                                            v-model="password"
                                            :rules="passRules"
                                            :label="$t('Password') + '*'"
                                            :hint="$t('password_of_school_account')"
                                            type="password"
                                            required
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="blue darken-1"
                                text
                                @click="schoolDialog = false"
                            >
                                {{ $t('close') }}
                            </v-btn>
                            <v-btn
                                color="blue darken-1"
                                text
                                @click="createSchool"
                            >
                                {{ $t('save') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-form>
            </v-dialog>
        </v-row>
        <v-dialog
            v-model="coinsDialog"
            persistent
            no-click-animation
            max-width="460"
            @keydown.esc="coinsDialog = false"
        >
            <v-card v-if="coinsSchool">
                <v-card-title>
                    <v-icon color="primary" class="me-2">mdi-hand-coin</v-icon>
                    <span class="text-h6">{{ $t('add_coins_to', { name: coinsSchool.name }) }}</span>
                </v-card-title>
                <v-card-text>
                    <div class="mb-3">
                        {{ $t('current_balance') }}:
                        <strong>{{ coinsSchool.balance || 0 }}</strong> {{ $t('coins') }}
                    </div>
                    <v-form ref="coinsForm" v-model="coinsValid" lazy-validation>
                        <v-text-field
                            v-model="coinsToAdd"
                            :rules="coinsRules"
                            :label="$t('coins_to_add')"
                            type="number"
                            autofocus
                            required
                        ></v-text-field>
                    </v-form>
                    <v-alert v-if="newCoinTotal !== null" type="info" dense text>
                        {{ $t('new_balance_will_be', { total: newCoinTotal }) }}
                    </v-alert>

                    <v-divider class="my-3"></v-divider>

                    <div class="text-subtitle-2 mb-2">{{ $t('billing_for_this_school') }}</div>
                    <v-select
                        v-model="billingMode"
                        :items="billingModes"
                        item-text="text"
                        item-value="value"
                        :label="$t('who_pays')"
                        dense
                    ></v-select>
                    <v-text-field
                        v-model="schoolRate"
                        type="number"
                        :label="$t('coins_per_student_per_day')"
                        :hint="$t('leave_blank_for_system_default', { rate: systemCoinRate })"
                        persistent-hint
                        clearable
                        dense
                    ></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text :disabled="isSavingCoins" @click="coinsDialog = false">
                        {{ $t('close') }}
                    </v-btn>
                    <v-btn text :loading="isSavingBilling" @click="saveBilling">
                        {{ $t('save_billing') }}
                    </v-btn>
                    <v-btn color="primary" text :loading="isSavingCoins" @click="addCoins">
                        {{ $t('add_coins') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <add-student-dialog
            v-model="studentDialog"
            :parents="schoolParents"
            @added="loadUsers"
        ></add-student-dialog>
        <map-students-dialog
            v-model="mapDialog"
            :students="studentsToMap"
            @mapped="onMapped"
            @partial-failure="showMapFailures"
        ></map-students-dialog>
        <add-parent-dialog
            v-model="parentDialog"
            @created="loadUsers"
        ></add-parent-dialog>
        <add-driver-dialog
            v-model="driverDialog"
            @created="loadUsers"
        ></add-driver-dialog>
        <guardian-students-dialog
            v-model="guardianStudentsDialog"
            :guardian="guardianForStudents"
            @changed="reload"
        ></guardian-students-dialog>
        <undo-bar
            :pending="pendingUndo"
            :seconds-left="undoSecondsLeft"
            @undo="undoPendingRemove"
        ></undo-bar>
        <!-- a mapping is already saved when this appears, so Undo writes the
             previous mapping back rather than cancelling anything -->
        <undo-bar
            :pending="mappingUndo"
            :seconds-left="0"
            @undo="undoMapping"
        ></undo-bar>
        <confirm-notify-dialog
            v-model="unmapDialog"
            :title="$t('unmap')"
            :text="$t('confirm_unmap_students', { count: unmapTargets.length })"
            :confirm-text="$t('yes')"
            icon="mdi-map-marker-off"
            icon-color="error"
            @confirm="onUnmapConfirmed"
        ></confirm-notify-dialog>
        <confirm-notify-dialog
            v-model="statusDialog"
            :title="statusDialogTitle"
            :text="statusDialogText"
            :confirm-text="$t('yes')"
            :icon="statusUser && statusUser.status_id == 1 ? 'mdi-account-off' : 'mdi-account-check'"
            :icon-color="statusUser && statusUser.status_id == 1 ? 'error' : 'success'"
            @confirm="onStatusConfirmed"
        ></confirm-notify-dialog>
        <!-- the admin sets the password and hands it over; no link to wait for -->
        <v-dialog
            v-model="passwordDialog"
            persistent
            no-click-animation
            max-width="460"
            @keydown.esc="passwordDialog = false"
        >
            <v-form ref="passwordForm" v-model="passwordValid" lazy-validation>
                <v-card v-if="passwordUser">
                    <v-card-title>
                        <v-icon color="primary" class="me-2">mdi-lock-reset</v-icon>
                        <span class="text-h6">{{ $t('set_password_for', { name: passwordUser.name }) }}</span>
                    </v-card-title>
                    <v-card-text>
                        <p>{{ $t('set_password_hint', { email: passwordUser.email }) }}</p>
                        <v-text-field
                            v-model="newPassword"
                            :rules="passwordRules"
                            :label="$t('Password')"
                            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                            :type="showPassword ? 'text' : 'password'"
                            autofocus
                            @click:append="showPassword = !showPassword"
                        ></v-text-field>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text :disabled="isSavingPassword" @click="passwordDialog = false">
                            {{ $t('close') }}
                        </v-btn>
                        <v-btn color="primary" text :loading="isSavingPassword" @click="savePassword">
                            {{ $t('save') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>
    </div>
</template>

<script>
import usersTable from "./users-table.vue";
import {
    mdiAccountCheck,
    mdiAccountOff,
    mdiAirplane,
    mdiMotionPause,
    mdiAccountClock,
    mdiAccountQuestion,
} from "@mdi/js";

import VueElementLoading from "vue-element-loading";
import auth from "@/services/AuthService";
import CreateButton from "@/components/CreateButton";
import AddParentDialog from "./add-parent-dialog.vue";
import AddStudentDialog from "./add-student-dialog.vue";
import AddDriverDialog from "./add-driver-dialog.vue";
import GuardianStudentsDialog from "./guardian-students-dialog.vue";
import MapStudentsDialog from "./map-students-dialog.vue";
import UndoBar from "@/components/UndoBar";
import ConfirmNotifyDialog from "@/components/ConfirmNotifyDialog";
import undoable from "@/mixins/undoable";
import { exportRowsToCsv } from "@/utils/download";
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
export default {
    mixins: [undoable],
    components: {
        VueElementLoading,
        usersTable,
        CreateButton,
        UndoBar,
        ConfirmNotifyDialog,
        AddParentDialog,
        AddStudentDialog,
        AddDriverDialog,
        GuardianStudentsDialog,
        MapStudentsDialog
    },
    data() {
        return {
            mode: null,
            file: null,
            userType: "",
            users: [],
            //one page at a time now, with the counts the tabs label themselves with
            rows: [],
            total: 0,
            counts: {},
            isLoading: false,
            lastQuery: null,
            availableBuses: [],
            dialog: false,
            busesDialog: false,
            busesForStudentDialog: false,
            loadingBuses: false,
            isSubmit: false,
            isSubmit: false,
            selectedUser: null,
            selectedDriver: null,
            selectedStudent: null,
            selectedStudentMorning: null,
            tabs: [],
            driverTabs: [
                { idx: 0, title: "Active", icon: mdiAirplane },
                { idx: 1, title: "Suspended", icon: mdiMotionPause },
                { idx: 2, title: "Under Review", icon: mdiAccountClock },
            ],
            studentTabs: [
                { idx: 0, title: "Active", icon: mdiAirplane },
                { idx: 1, title: "Suspended", icon: mdiMotionPause },
                { idx: 2, title: "Under Review", icon: mdiAccountClock },
                { idx: 3, title: "Out of coins", icon: mdiAccountQuestion },
            ],
            defaultTabs: [
                { idx: 0, title: "Active", icon: mdiAirplane },
                { idx: 1, title: "Suspended", icon: mdiMotionPause },
            ],
            active_tab: null,
            name: "",
            nameRules: [
                (v) => !!v || this.$t("school_name_required"),
                (v) =>
                    (v && v.length <= 15) ||
                    this.$t("school_name_max_length"),
            ],
            email: "",
            emailRules: [
                (v) => !!v || this.$t("E-mail is required"),
                (v) => /.+@.+\..+/.test(v) || this.$t("E-mail must be valid"),
            ],
            password: "",
            passRules: [(v) => !!v || this.$t("password_required")],
            schoolDialog: false,
            parentDialog: false,
            studentDialog: false,
            driverDialog: false,
            mapDialog: false,
            studentsToMap: [],
            schoolParents: [],
            coinsDialog: false,
            coinsSchool: null,
            coinsToAdd: "",
            coinsValid: true,
            isSavingCoins: false,
            isSavingBilling: false,
            billingMode: "school_then_parent",
            schoolRate: null,
            systemCoinRate: 1,
            coinsRules: [(v) => /^[1-9][0-9]*$/.test(v) || this.$t("enter_a_positive_number")],
            passwordDialog: false,
            passwordUser: null,
            newPassword: "",
            showPassword: false,
            passwordValid: true,
            isSavingPassword: false,
            passwordRules: [
                (v) => !!v || this.$t("password_required"),
                (v) => (v && v.length >= 6) || this.$t("password_min_length"),
            ],
            statusDialog: false,
            statusUser: null,
            unmapDialog: false,
            unmapTargets: [],
            guardianStudentsDialog: false,
            guardianForStudents: null,
            mappingUndoTimer: null,
            //a mapping change that can still be put back
            mappingUndo: null,
            notifyOnBusChange: false,
            busChangeReason: "",
            valid: true,
        };
    },
    computed: {
        //the rows on screen; the tab decides which status was asked for
        currentTabUsers() {
            return this.rows;
        },
        //tab index -> status_id. 1 active, 3 suspended, 4 under review,
        //5 out of coins
        tabStatus() {
            return { 0: 1, 1: 3, 2: 4, 3: 5 }[this.active_tab] || 1;
        },
        statusDialogTitle() {
            if (!this.statusUser) return "";
            const what = this.userType.slice(0, -1);
            return (this.statusUser.status_id != 1 ? this.$t("activate") : this.$t("suspend")) + " " + what;
        },
        statusDialogText() {
            if (!this.statusUser) return "";
            return this.statusUser.status_id != 1
                ? this.$t("confirm_activate_user", { name: this.statusUser.name })
                : this.$t("confirm_suspend_user", { name: this.statusUser.name });
        },
        billingModes() {
            return [
                { value: "school_then_parent", text: this.$t("billing_school_then_parent") },
                { value: "school", text: this.$t("billing_school") },
                { value: "parent", text: this.$t("billing_parent") },
            ];
        },
        newCoinTotal() {
            if (!this.coinsSchool) return null;
            const add = parseInt(this.coinsToAdd, 10);
            if (isNaN(add) || add <= 0) return null;
            return (parseInt(this.coinsSchool.balance, 10) || 0) + add;
        },
        translatedDriverTabs() {
            return this.driverTabs.map(tab => ({
                ...tab,
                title: this.$t(tab.title.toLowerCase().replace(' ', '_'))
            }));
        },
        translatedStudentTabs() {
            return this.studentTabs.map(tab => ({
                ...tab,
                title: this.$t(tab.title.toLowerCase().replace(' ', '_').replace('out_of coins', 'out_of_coins'))
            }));
        },
        translatedDefaultTabs() {
            return this.defaultTabs.map(tab => ({
                ...tab,
                title: this.$t(tab.title.toLowerCase().replace(' ', '_'))
            }));
        },
        footerProps() {
            return {
                itemsPerPageText: this.$t("rows_per_page"),
            }
        }
    },
    watch: {
        $route(to, from) {
            this.userType = to.name;
            //a different list entirely: page 5 of the last one means nothing here
            if (this.lastQuery) {
                this.lastQuery = { ...this.lastQuery, page: 1 };
            }
            this.updateTabs();
            if (this.userType === "drivers") {
                this.active_tab = parseInt(localStorage.tabIdxDrivers);
            } else if (this.userType === "students") {
                this.active_tab = parseInt(localStorage.tabIdxStudents);
            } else if (this.userType === "parents") {
                this.active_tab = parseInt(localStorage.tabIdxParents);
            } else if (this.userType === "schools") {
                this.active_tab = parseInt(localStorage.tabIdxSchools);
            }
            this.loadUsers();
        },
        active_tab: function (newVal, oldVal) {
            //each tab is a status filter now, so it is a different question
            if (oldVal !== null && oldVal !== undefined && newVal !== oldVal) {
                this.fetchPage();
            }
            if (this.userType === "drivers") {
                localStorage.tabIdxDrivers = newVal;
            } else if (this.userType === "parents") {
                localStorage.tabIdxParents = newVal;
            } else if (this.userType === "students") {
                localStorage.tabIdxStudents = newVal;
            } else if (this.userType === "schools") {
                localStorage.tabIdxSchools = newVal;
            }
        },
        //watch language change to update tabs
        '$i18n.locale'(newVal, oldVal) {
            this.updateTabs();
        },
    },
    mounted() {
        this.mode = auth.getMode()
        this.userType = this.$router.currentRoute.name;
        this.updateTabs();
        if (this.userType === "drivers") {
            this.active_tab = parseInt(localStorage.tabIdxDrivers);
        } else if (this.userType === "parents") {
            this.active_tab = parseInt(localStorage.tabIdxParents);
        } else if (this.userType === "students") {
            this.active_tab = parseInt(localStorage.tabIdxStudents);
        } else if (this.userType === "schools") {
            this.active_tab = parseInt(localStorage.tabIdxSchools);
        }
        this.loadUsers();
        if (this.userType === "students") {
            this.loadSchoolParents();
        }
        //load buses for drivers
        if (this.userType === "drivers") {
            this.loadAvailableBuses();
        }
    },
    methods: {
        manageGuardianStudents(guardian) {
            this.guardianForStudents = guardian;
            this.guardianStudentsDialog = true;
        },
        showMapStudents(students) {
            this.studentsToMap = students;
            this.mapDialog = true;
        },
        /*
         * Taking children off a route. Both directions go at once, which is
         * what "this class has left" means; doing one direction at a time
         * through the mapping dialog was two passes over the same selection.
         */
        unmapStudents(students) {
            this.unmapTargets = students;
            this.unmapDialog = true;
        },
        onUnmapConfirmed({ notify, reason }) {
            const students = this.unmapTargets || [];
            if (!students.length) return;

            const ids = students.map((student) => student.id);
            this.isSubmit = true;

            //pickup and drop-off are separate mappings server side
            axios
                .post("/school-students/clear-mapping", {
                    student_ids: ids,
                    pick_up: true,
                    notify: notify,
                    reason: reason,
                })
                .then((first) =>
                    axios
                        .post("/school-students/clear-mapping", {
                            student_ids: ids,
                            pick_up: false,
                            //the family has already been told once
                            notify: false,
                        })
                        .then((second) => {
                            //one snapshot covers both, since each row holds all
                            //four columns
                            return first.data.snapshot && first.data.snapshot.length
                                ? first.data.snapshot
                                : second.data.snapshot || [];
                        })
                )
                .then((snapshot) => {
                    this.$notifySuccess(
                        this.$t("students_unmapped", { count: ids.length })
                    );
                    this.onMapped({ snapshot: snapshot });
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_mapping_students"));
                })
                .then(() => {
                    this.isSubmit = false;
                    this.unmapTargets = [];
                });
        },
        /*
         * Mapping is the one thing here done to hundreds of children at once,
         * and the stop and trip each of them had a moment ago is nowhere on
         * screen once it has been overwritten. The server hands back what it
         * replaced; this holds it while the bar is up and writes it back if
         * Undo is pressed.
         */
        onMapped(result) {
            this.reload();

            const snapshot = (result && result.snapshot) || [];
            if (!snapshot.length) return;

            this.mappingUndo = {
                label: "",
                message: this.$t("mapping_changed_for", { count: snapshot.length }),
                snapshot: snapshot,
            };

            //long enough to notice, short enough not to sit there for ever
            if (this.mappingUndoTimer) clearTimeout(this.mappingUndoTimer);
            this.mappingUndoTimer = setTimeout(() => {
                this.mappingUndo = null;
            }, 12000);
        },
        undoMapping() {
            const pending = this.mappingUndo;
            this.mappingUndo = null;
            if (!pending) return;

            this.isSubmit = true;
            axios
                .post("/school-students/restore-mapping", { snapshot: pending.snapshot })
                .then((response) => {
                    this.$notifySuccess(
                        this.$t("mapping_restored", {
                            count: (response.data.restored || []).length,
                        })
                    );
                    this.reload();
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_mapping_students"));
                })
                .then(() => {
                    this.isSubmit = false;
                });
        },
        showMapFailures(failed) {
            const lines = failed.map((f) => `${f.name}: ${f.message}`).join("<br>");
            this.$swal({
                title: this.$t("some_students_were_not_mapped"),
                html: lines,
                icon: "warning",
            });
        },
        //the add-student dialog offers existing parents to attach the child to
        loadSchoolParents() {
            axios
                .get("/users/all-parents")
                .then((response) => {
                    this.schoolParents = response.data.parents || [];
                })
                .catch(() => {
                    //the dialog still accepts a typed address without this
                });
        },
        showAddCoins(school) {
            this.coinsSchool = school;
            this.coinsToAdd = "";
            const settings = school.school_settings || {};
            this.billingMode = settings.billing_mode || "school_then_parent";
            this.schoolRate = settings.coins_per_student_per_day || null;
            this.coinsDialog = true;
            if (this.$refs.coinsForm) this.$refs.coinsForm.resetValidation();
            this.loadSystemCoinRate();
        },
        loadSystemCoinRate() {
            axios
                .get("/settings/all")
                .then((response) => {
                    const settings = Array.isArray(response.data) ? response.data[0] : response.data;
                    if (settings && settings.coins_per_student_per_day) {
                        this.systemCoinRate = settings.coins_per_student_per_day;
                    }
                })
                .catch(() => {
                    //the hint falls back to 1, which is the server default anyway
                });
        },
        saveBilling() {
            const school = this.coinsSchool;
            this.isSavingBilling = true;
            axios
                .post("/users/school-billing", {
                    school_id: school.id,
                    billing_mode: this.billingMode,
                    coins_per_student_per_day: this.schoolRate === "" ? null : this.schoolRate,
                })
                .then((response) => {
                    this.$set(school, "school_settings", response.data.school_settings);
                    this.$notifySuccess(this.$t("billing_updated"));
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_saving_billing"));
                })
                .then(() => {
                    this.isSavingBilling = false;
                });
        },
        // Increments server-side and records the grant, so a deduction landing at
        // the same moment cannot be overwritten.
        addCoins() {
            if (!this.$refs.coinsForm.validate()) return;

            const school = this.coinsSchool;
            const add = parseInt(this.coinsToAdd, 10);
            if (isNaN(add) || add <= 0) return;

            this.isSavingCoins = true;
            axios
                .post("/users/add-coins", {
                    school_id: school.id,
                    coins: add,
                })
                .then((response) => {
                    //the server returns the authoritative balance
                    this.$set(school, "balance", response.data.balance);
                    this.$notifySuccess(
                        this.$t("coins_added", { count: this.coinsToAdd, name: school.name })
                    );
                    this.coinsDialog = false;
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_adding_coins"));
                })
                .then(() => {
                    this.isSavingCoins = false;
                });
        },
        validate() {
            return this.$refs.form.validate();
        },
        // A separate Firebase app, so signing the new school in does not sign the
        // admin out of their own session. firebase.auth() is the primary app and
        // createUserWithEmailAndPassword() switches the signed-in user on it.
        schoolCreatorAuth() {
            const existing = firebase.apps.find((app) => app.name === "schoolCreator");
            const app = existing || firebase.initializeApp(firebase.app().options, "schoolCreator");
            return app.auth();
        },
        async createSchool() {
            if (!this.validate()) return;

            const auth = this.schoolCreatorAuth();
            let createdUser = null;

            this.isSubmit = true;
            try {
                const result = await auth.createUserWithEmailAndPassword(this.email, this.password);
                createdUser = result.user;
                const token = await createdUser.getIdToken(true);

                try {
                    const response = await axios.post('/auth/loginViaToken', {
                        'device_name': this.deviceName(),
                        token,
                        'role': 2,
                        'name': this.name,
                        'email': this.email,
                    });

                    //the list comes from the server now, so take it again
                    this.reload();

                    this.$notifySuccess(this.$t("school_created"));
                    this.schoolDialog = false;
                    createdUser = null; //kept: the school owns it now
                } catch (serverError) {
                    // The Firebase account exists but the school row does not. Left
                    // behind, that address can never be used again -- the next
                    // attempt fails with "email already in use" and the real cause
                    // stays hidden. So undo it and let the admin retry cleanly.
                    try {
                        await createdUser.delete();
                        createdUser = null;
                    } catch (cleanupError) {
                        console.log("could not roll back the Firebase account", cleanupError);
                    }
                    throw serverError;
                }
            } catch (error) {
                this.$notifyError(error, this.$t("error_creating_school"));
            } finally {
                //never leave the new school signed in on the helper app
                try {
                    await auth.signOut();
                } catch (e) {
                    //nothing to sign out of
                }
                this.isSubmit = false;
            }
        },
        deviceName() {
            const meta = this.$browserDetect && this.$browserDetect.meta;
            return meta ? `${meta.name} - v${meta.version}` : "Admin panel";
        },
        showCreateSchoolDialog() {
            this.name = "";
            this.email = "";
            this.password = "";
            this.schoolDialog = true;
        },
        updateTabs() {
            if (this.userType === "drivers") {
                this.tabs = this.translatedDriverTabs;
            } else if(this.userType === "students") {
                this.tabs = this.translatedStudentTabs;
            } else {
                this.tabs = this.translatedDefaultTabs;
            }
        },
        capitalizeFirstLetter(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        },
        getIcon(userType) {
            switch (userType) {
                case "schools":
                    return "mdi-school";
                case "students":
                    return "mdi-badge-account-outline";
                case "drivers":
                    return "mdi-account-tie-hat";
                case "guardians":
                    return "mdi-account-group-outline";
                default:
                    break;
            }
        },
        /*
         * One page of the list, chosen by the database.
         *
         * The whole list used to arrive here and be paged, searched and sorted
         * in the browser - fine for forty students, hopeless for four thousand,
         * where every child, their parent and their bus assignments were sent
         * whether or not anyone would scroll that far. The table hands up what
         * it needs and this asks for exactly that.
         */
        fetchPage(query) {
            //the table emits on every option change, including ones that make
            //no difference to what is being asked for
            if (query) {
                this.lastQuery = query;
            }
            //the table mounts before this screen knows which list it is, and
            //asking for type "" is a validation error rather than a list
            if (!this.userType) {
                return;
            }
            const params = Object.assign(
                {
                    type: this.userType,
                    status_id: this.tabStatus,
                },
                this.lastQuery || {}
            );

            this.isLoading = true;
            axios
                .get("/users/list", { params: params })
                .then((response) => {
                    this.rows = response.data.data || [];
                    this.total = response.data.total || 0;
                    this.counts = response.data.counts || {};
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_retrieving_users"));
                })
                .then(() => {
                    this.isLoading = false;
                });
        },
        tabCount(tabIdx) {
            const status = { 0: 1, 1: 3, 2: 4, 3: 5 }[tabIdx];
            return this.counts[status] || 0;
        },
        //after something changed the list out from under us
        reload() {
            this.fetchPage();
        },
        loadUsers() {
            this.fetchPage();
        },
        deleteSchool(school)
        {

            this.$swal
                .fire({
                    title: this.$t("delete_school"),
                    text: this.$t("confirm_delete_school", { name: school.name }),
                    icon: "warning",
                    showCancelButton: true,
                    cancelButtonText: this.$t("cancel"),
                    confirmButtonText: this.$t("yes_delete_school"),
                    confirmButtonColor: "#f44336",
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        this.deleteSchoolServer(school);
                    }
                });
        },
        deleteSchoolServer(school)
        {
            this.isSubmit = true;
            axios
                .delete("/users/delete-school", {
                    data: {
                        school_id: school.id,
                    },
                })
                .then((response) => {
                    this.isSubmit = false;
                    this.$notify({
                        title: "Success",
                        text: this.$t("school_deleted"),
                        type: "success",
                    });
                    this.reload();
                })
                .catch((error) => {
                    this.isSubmit = false;
                    this.$notify({
                        title: "Error",
                        text: this.$t("error_title"),
                        type: "error",
                    });
                    console.log(error);
                    this.$swal(this.$t("error_title"), error.response.data.message, "error");
                });
        },
        loginAsSchool(school)
        {

            this.$swal
                .fire({
                    title: this.$t("login_as_school"),
                    text: this.$t("confirm_login_as_school", { name: school.name }),
                    icon: "warning",
                    showCancelButton: true,
                    cancelButtonText: this.$t("cancel"),
                    confirmButtonText: this.$t("yes_login"),
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        this.loginAsSchoolServer(school);
                    }
                });
        },
        loginAsSchoolServer(school)
        {
            //post to backend login-from-admin-to-school
            this.isSubmit = true;
            axios
                .post("/auth/login-from-admin-to-school", {
                    school_id: school.id,
                    device_name: `${vm.$browserDetect.meta.name  }- v${  vm.$browserDetect.meta.version}`
                })
                .then((response) => {
                    this.isSubmit = false;
                    this.$notify({
                        title: "Success",
                        text: this.$t("logged_in_as", { name: school.name }),
                        type: "success",
                    });
                    const ourToken = response.data.token
                    let user = response.data.user_data
                    const userRole = user.role_id
                    const simple_mode = response.data.simple_mode
                    const freshToken = ourToken.split('|')[1]
                    localStorage.setItem('freshToken', freshToken)
                    localStorage.setItem('userRole', userRole)
                    localStorage.setItem('simple_mode', simple_mode)
                    axios.defaults.headers.common['Authorization'] = `Bearer ${freshToken}`
                    //redirect to school dashboard
                    this.$router.push({
                        name: "school-dashboard",
                    });
                })
                .catch((error) => {
                    this.isSubmit = false;
                    this.$notify({
                        title: "Error",
                        text: this.$t("error_title"),
                        type: "error",
                    });
                    console.log(error);
                    this.$swal(this.$t("error_title"), error.response.data.message, "error");
                });
        },
        viewUser(user) {
            let userType = this.userType.slice(0, -1);
            let routeName = "view-" + userType;
            this.$router.push({
                name: routeName,
                params: {
                    user_id: user.id,
                },
            });
        },
        showStudentLocation(student) {
            this.$router.push({
                name: "student-location",
                params: {
                    student_id: student.id,
                },
            });
        },
        editUser(user) {
            let userType = this.userType.slice(0, -1);
            let routeName = "edit-" + userType;
            this.$router.push({
                name: routeName,
                params: {
                    user_id: user.id,
                },
            });
        },
        suspendActivateUser(user, index) {
            //the confirm and the "tell the family" question are the same
            //question, so they are asked in the same place
            this.statusUser = user;
            this.statusDialog = true;
        },
        onStatusConfirmed({ notify, reason }) {
            const user = this.statusUser;
            if (!user) return;

            const target = user.status_id != 1 ? 1 : 3;
            this.isSubmit = true;
            axios
                .post("/users/set-status", {
                    user_id: user.id,
                    status_id: target,
                    notify: notify,
                    reason: reason,
                })
                .then(() => {
                    this.$notifySuccess(
                        target === 1 ? this.$t("user_activated") : this.$t("user_suspended")
                    );
                    this.reload();
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_title"));
                })
                .then(() => {
                    this.isSubmit = false;
                    this.statusUser = null;
                });
        },
        /*
         * The admin sets the password and passes it on.
         *
         * This used to email a Firebase reset link, which is no help at all for
         * the people it was most needed for: a parent whose address was typed
         * wrong never received it, a driver may not have mail set up on the
         * phone they drive with, and either way the admin could not tell whether
         * it had arrived. Setting it here is one step and the admin knows the
         * result.
         */
        showSetPassword(user) {
            this.passwordUser = user;
            this.newPassword = "";
            this.showPassword = false;
            this.passwordDialog = true;
            if (this.$refs.passwordForm) this.$refs.passwordForm.resetValidation();
        },
        savePassword() {
            if (!this.$refs.passwordForm.validate()) return;

            const user = this.passwordUser;
            this.isSavingPassword = true;
            axios
                .post("/users/update-password", {
                    user_id: user.id,
                    password: this.newPassword,
                })
                .then(() => {
                    this.$notifySuccess(this.$t("password_set", { name: user.name }));
                    this.passwordDialog = false;
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_setting_password"));
                })
                .then(() => {
                    this.isSavingPassword = false;
                });
        },
        loadAvailableBuses() {
            this.loadingBuses = true;
            this.availableBuses = [];
            axios
                .get("/drivers/available-buses")
                .then((response) => {
                    this.availableBuses = response.data;
                })
                .catch((error) => {
                    this.$notify({
                        title: "Error",
                        text: this.$t("error_retrieving_buses"),
                        type: "error",
                    });
                    console.log(error);
                    this.$swal(this.$t("error_title"), error.response.data.message, "error");
                })
                .then(() => {
                    this.loadingBuses = false;
                });
        },
        loadAllBuses(is_morning) {
            this.loadingBuses = true;
            this.availableBuses = [];
            axios
                .get("/drivers/all-buses")
                .then((response) => {
                    this.availableBuses = response.data;
                    if(is_morning==1)
                    {
                        //add available seats to the buses
                        this.availableBuses.forEach(bus => {
                            bus.available_seats = bus.available_morning_seats;
                        });
                    }
                    else{
                        //add available seats to the buses
                        this.availableBuses.forEach(bus => {
                            bus.available_seats = bus.available_afternoon_seats;
                        });
                    }
                })
                .catch((error) => {
                    this.$notify({
                        title: "Error",
                        text: this.$t("error_retrieving_buses"),
                        type: "error",
                    });
                    console.log(error);
                    this.$swal(this.$t("error_title"), error.response.data.message, "error");
                })
                .then(() => {
                    this.loadingBuses = false;
                });
        },
        assignBus(item) {
            this.selectedDriver = item;
            this.busesDialog = true;
            this.loadAvailableBuses();
        },
        setStudentBus(item, is_morning)
        {
            this.selectedStudentMorning = is_morning;
            this.selectedStudent = item;
            this.busesForStudentDialog = true;
            this.loadAllBuses(is_morning);
        },
        closeStudentBusDialog() {
            this.busesForStudentDialog = false;
            this.loadingBuses = false;
            this.availableBuses = [];
        },
        closeBusDialog() {
            this.busesDialog = false;
            this.loadingBuses = false;
            this.availableBuses = [];
        },
        assignBusToStudent(bus) {
            this.loadingBuses = true;
            axios
                .post("/users/assign-student-bus", {
                    student_id: this.selectedStudent.id,
                    bus_id: bus.id,
                    is_morning: this.selectedStudentMorning,
                    notify: this.notifyOnBusChange,
                })
                .then((response) => {
                    console.log(response);
                    this.selectedStudent.student_settings = response.data.student_settings;
                    this.loadingBuses = false;
                    this.closeStudentBusDialog();
                    this.$notify({
                        title: "Success",
                        text: this.$t("bus_assigned_to_student"),
                        type: "success",
                    });
                })
                .catch((error) => {
                    this.isSubmit = false;
                    this.$notify({
                        title: "Error",
                        text: this.$t("error_title"),
                        type: "error",
                    });
                    console.log(error);
                    this.$swal(this.$t("error_title"), error.response.data.message, "error");
                })
                .then(() => {
                    this.closeStudentBusDialog();
                });
        },
        assignBusToDriver(bus) {
            this.loadingBuses = true;
            axios
                .post("/drivers/assign-bus", {
                    driver_id: this.selectedDriver.id,
                    bus_id: bus.id,
                })
                .then((response) => {
                    console.log(response);
                    this.loadingBuses = false;
                    this.selectedDriver.bus = bus;
                    console.log(this.selectedDriver);
                    this.closeBusDialog();
                    this.$notify({
                        title: "Success",
                        text: this.$t("bus_assigned_to_driver"),
                        type: "success",
                    });
                })
                .catch((error) => {
                    this.isSubmit = false;
                    this.$notify({
                        title: "Error",
                        text: this.$t("error_title"),
                        type: "error",
                    });
                    console.log(error);
                    this.$swal(this.$t("error_title"), error.response.data.message, "error");
                })
                .then(() => {
                    this.closeBusDialog();
                });
        },
        unAssignBus(item) {
            this.$swal
                .fire({
                    title: this.$t("unassign_bus_title"),
                    text: this.$t("confirm_unassign_bus", {
                        driver: item.name,
                        license: item.bus.license
                    }),
                    icon: "error",
                    showCancelButton: true,
                    cancelButtonText: this.$t("cancel"),
                    confirmButtonText: "Yes, delete it!",
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        this.unassignBusFromDriver(item);
                    }
                });
        },
        unassignBusFromDriver(driver) {
            this.isSubmit = true;
            axios
                .post("/drivers/unassign-bus", {
                    driver_id: driver.id,
                })
                .then((response) => {
                    this.isSubmit = false;
                    driver.bus = null;
                    this.$notify({
                        title: "Success",
                        text: this.$t("bus_unassigned_from_driver"),
                        type: "success",
                    });
                })
                .catch((error) => {
                    this.isSubmit = false;
                    this.$notify({
                        title: "Error",
                        text: this.$t("error_title"),
                        type: "error",
                    });
                    console.log(error);
                    this.$swal(this.$t("error_title"), error.response.data.message, "error");
                })
                .then(() => {
                    this.closeBusDialog();
                });
        },
        /*
         * Exports what the filter matches, not the twenty-five rows on screen.
         * Now that the list is paged, "export" would otherwise quietly mean
         * "export this page", which is the wrong answer to the question a
         * school is asking when it presses it.
         */
        exportCurrentTab() {
            const columns = this.exportColumns();
            const stamp = new Date().toISOString().slice(0, 10);
            const params = Object.assign({}, this.lastQuery || {}, {
                type: this.userType,
                status_id: this.tabStatus,
                page: 1,
                //the endpoint caps at 500; beyond that it is a report rather
                //than a spreadsheet, and the admin is told what they got
                per_page: 500,
            });

            this.isSubmit = true;
            axios
                .get("/users/list", { params: params })
                .then((response) => {
                    const rows = response.data.data || [];
                    const total = response.data.total || rows.length;

                    exportRowsToCsv(rows, columns, `${this.userType}-${stamp}`);

                    if (total > rows.length) {
                        this.$notifySuccess(
                            this.$t("exported_first_n", { count: rows.length, total: total })
                        );
                    }
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_retrieving_users"));
                })
                .then(() => {
                    this.isSubmit = false;
                });
        },
        exportColumns() {
            const status = {
                text: this.$t("Status"),
                value: (user) => this.$t(this.statusKey(user.status_id)),
            };
            const created = {
                text: this.$t("Created"),
                value: (user) => this.$moment(user.created_at).format("YYYY-MM-DD HH:mm"),
            };

            if (this.userType === "students") {
                return [
                    { text: this.$t("student_id"), value: "student_identification" },
                    { text: this.$t("Name"), value: "name" },
                    { text: this.$t("parent_name"), value: "parent.name" },
                    { text: this.$t("parent_email"), value: "parent.email" },
                    {
                        text: this.$t("route"),
                        value: (student) =>
                            student.student_settings &&
                            student.student_settings.pickup_trip_id &&
                            student.student_settings.drop_off_trip_id
                                ? this.$t("mapped")
                                : this.$t("not_mapped"),
                    },
                    status,
                    created,
                ];
            }

            if (this.userType === "drivers") {
                return [
                    { text: this.$t("id"), value: "id" },
                    { text: this.$t("Name"), value: "name" },
                    { text: this.$t("Email"), value: "email" },
                    { text: this.$t("phone_number"), value: "tel_number" },
                    {
                        text: this.$t("bus"),
                        value: (driver) => (driver.bus ? driver.bus.license : ""),
                    },
                    status,
                    created,
                ];
            }

            if (this.userType === "schools") {
                return [
                    { text: this.$t("id"), value: "id" },
                    { text: this.$t("Name"), value: "name" },
                    { text: this.$t("Email"), value: "email" },
                    { text: this.$t("coins"), value: (school) => school.balance || 0 },
                    status,
                    created,
                ];
            }

            return [
                { text: this.$t("id"), value: "id" },
                { text: this.$t("Name"), value: "name" },
                { text: this.$t("Email"), value: "email" },
                { text: this.$t("phone_number"), value: "tel_number" },
                {
                    text: this.$t("role"),
                    value: (user) => (user.role_id === 4 ? this.$t("parent") : this.$t("guardian")),
                },
                status,
                created,
            ];
        },
        statusKey(statusId) {
            return {
                1: "active",
                2: "pending",
                3: "suspended",
                4: "under_review",
                5: "out_of_coins",
            }[statusId] || "active";
        },

        /*
         * Deleting an account: the row goes now, the request goes in a few
         * seconds, and Undo in between costs nothing because nothing has
         * happened yet. That is safer than the confirm dialog it replaces - a
         * dialog is answered from memory, an undo bar is answered from the
         * screen, where the row is visibly gone.
         *
         * A parent is the exception and still asks first: it can take children
         * with it, which is not something to discover from an undo bar.
         */
        deleteUser(user) {
            if (user.role_id === 4) {
                this.$confirmAction({
                    title: this.$t("delete_user_title", { name: user.name }),
                    text: this.$t("confirm_delete_parent", { name: user.name }),
                    icon: "warning",
                    confirmText: this.$t("yes_delete"),
                }).then((confirmed) => {
                    if (confirmed) this.removeUser(user);
                });
                return;
            }

            this.removeUser(user);
        },
        removeUser(user) {
            this.undoableRemove({
                item: user,
                list: this.currentTabUsers,
                label: user.name,
                request: () => axios.post("/users/delete-user", { user_id: user.id }),
                onDone: (response) => {
                    const alsoRemoved = (response && response.data) || {};
                    this.$notifySuccess(
                        alsoRemoved.students_deleted
                            ? this.$t("user_deleted_with_students", {
                                  name: user.name,
                                  count: alsoRemoved.students_deleted,
                              })
                            : this.$t("user_deleted", { name: user.name })
                    );
                    //other tabs and counts move too, so take the list again
                    this.loadUsers();
                },
                onError: (error) => {
                    this.$notifyError(error, this.$t("error_deleting_user"));
                },
            });
        },

        //approving from the row: the same endpoint the detail screen posts to,
        //without having to open each person to press one button
        approveUser(user) {
            this.$confirmAction({
                title: this.$t("approve_user", { userType: user.name }),
                text: this.$t("confirm_approve_user", { userType: user.name }),
                icon: "question",
                confirmText: this.$t("yes_approve"),
            }).then((confirmed) => {
                if (!confirmed) return;
                this.takeAction(user, "Approved", 1);
            });
        },
        rejectUser(user) {
            this.$swal({
                input: "textarea",
                inputPlaceholder: this.$t("why_rejecting_user", { userType: user.name }),
                title: this.$t("reject_user", { userType: user.name }),
                html: this.$t("confirm_reject_user", { userType: user.name }),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: this.$t("yes_reject"),
                cancelButtonText: this.$t("cancel"),
            }).then((result) => {
                if (result.isConfirmed) {
                    this.takeAction(user, result.value, 2);
                }
            });
        },
        takeAction(user, reason, action) {
            this.isSubmit = true;
            axios
                .post("/users/take-action", {
                    user_id: user.id,
                    reason: reason,
                    action: action,
                })
                .then(() => {
                    this.$notifySuccess(this.$t("action_performed_successfully"));
                    this.loadUsers();
                })
                .catch((error) => {
                    this.$notifyError(error, this.$t("error_taking_action"));
                })
                .then(() => {
                    this.isSubmit = false;
                });
        },


    },
};
</script>
